/**
 * Canvas-level snapping for the top-level items (slides / doc cards / notes / images) as you drag them
 * around the infinite canvas. Pure world-px rect math — no React, no DOM. It draws nothing: the caller
 * animates the snapped position so the item "glides" into place (the smooth motion is the only feedback).
 *
 * Two snaps, both gated by PROXIMITY (you only ever snap to an item you're dragging *next to*, never to a
 * far / off-screen one — there are no guide lines to explain a distant snap):
 *  - GAP: a fixed 64px gap to a neighbour you overlap on the perpendicular axis (placed beside it).
 *  - ALIGN: same-edge only (left↔left, right↔right, top↔top, bottom↔bottom) — no center alignment.
 *
 * GAP wins its axis over ALIGN, and there is deliberately NO edge-*touch* snap (left↔right etc.) — placing
 * an item next to another should leave the 64px gap, not butt the two together.
 */

/** The world-space gap items snap to when placed next to each other. Constant world px → same at any zoom. */
export const CANVAS_SNAP_GAP = 128

// Screen-relative feel, divided by zoom so it's identical at every zoom. Looser than the in-slide editor
// (8/12) — the canvas items are large and you place them by feel, so a wider magnetic pull reads better:
// 16px to engage, 24px hysteresis to release.
const SNAP_THRESHOLD_SCREEN_PX = 16
const SNAP_RELEASE_SCREEN_PX = 24
// How far (world px, on top of the snap threshold) a candidate may sit and still be "near enough" to snap.
// It only GATES which items are eligible; the threshold still does the actual snapping — so a large value
// just means "don't align to things across the canvas", not "snap to anything within this radius".
const PROXIMITY_MARGIN = 200

const threshold = (scale: number) => SNAP_THRESHOLD_SCREEN_PX / Math.max(scale, 0.01)
const release = (scale: number) => SNAP_RELEASE_SCREEN_PX / Math.max(scale, 0.01)

export type CanvasRect = { id: string; x: number; y: number; width: number; height: number }

/** Per-axis hysteresis locks carried across drag frames (held position, or null when free). */
export type CanvasSnapSession = {
  gapX: number | null
  gapY: number | null
  alignX: number | null
  alignY: number | null
}

export const EMPTY_CANVAS_SNAP_SESSION: CanvasSnapSession = {
  gapX: null,
  gapY: null,
  alignX: null,
  alignY: null,
}

const axisStart = (r: CanvasRect, axis: 'x' | 'y') => (axis === 'x' ? r.x : r.y)
const axisSize = (r: CanvasRect, axis: 'x' | 'y') => (axis === 'x' ? r.width : r.height)
const perpStart = (r: CanvasRect, axis: 'x' | 'y') => (axis === 'x' ? r.y : r.x)
const perpSize = (r: CanvasRect, axis: 'x' | 'y') => (axis === 'x' ? r.height : r.width)

const intersectsExpanded = (a: CanvasRect, b: CanvasRect, margin: number) =>
  a.x < b.x + b.width + margin &&
  a.x + a.width + margin > b.x &&
  a.y < b.y + b.height + margin &&
  a.y + a.height + margin > b.y

/** Candidates the dragged rect is near enough to snap to (excludes itself + far items). */
const nearCandidates = (rect: CanvasRect, candidates: CanvasRect[], scale: number): CanvasRect[] => {
  const margin = PROXIMITY_MARGIN + threshold(scale)
  return candidates.filter((c) => c.id !== rect.id && intersectsExpanded(rect, c, margin))
}

type AxisSnap = { position: number | null; lock: number | null }

/**
 * Snap `start` to the nearest of `positions` within the snap threshold, with hysteresis: while a lock is
 * held it stays until `start` is pulled past the release distance. (`positions` are dragged-rect start
 * coordinates on the axis — i.e. the resulting snapped x or y.)
 */
const snapToPositions = (
  positions: number[],
  start: number,
  scale: number,
  lock: number | null,
): AxisSnap => {
  if (positions.length === 0) return { position: null, lock: null }
  if (lock != null && Math.abs(start - lock) <= release(scale)) return { position: lock, lock }
  const t = threshold(scale)
  let best: number | null = null
  let bestDist = Infinity
  for (const p of positions) {
    const d = Math.abs(start - p)
    if (d <= t && d < bestDist) {
      best = p
      bestDist = d
    }
  }
  return { position: best, lock: best }
}

/** Fixed-gap adjacency snap on one axis — only vs candidates overlapping on the PERPENDICULAR axis. */
const computeGapSnap = (
  near: CanvasRect[],
  rect: CanvasRect,
  axis: 'x' | 'y',
  gap: number,
  scale: number,
  lock: number | null,
): AxisSnap => {
  const size = axisSize(rect, axis)
  const ps = perpStart(rect, axis)
  const pz = perpSize(rect, axis)
  const positions: number[] = []
  for (const c of near) {
    const cps = perpStart(c, axis)
    const cpz = perpSize(c, axis)
    // Must overlap on the perpendicular axis — only then are the two items visually "next to" each other.
    if (cps >= ps + pz || cps + cpz <= ps) continue
    const cs = axisStart(c, axis)
    const csz = axisSize(c, axis)
    positions.push(cs + csz + gap) // dragged near-edge sits `gap` past the candidate's far edge
    positions.push(cs - gap - size) //  dragged far-edge sits `gap` before the candidate's near edge
  }
  return snapToPositions(positions, axisStart(rect, axis), scale, lock)
}

/** Same-edge alignment on one axis (NO center pairs, NO opposite-edge / touch pairs → items never butt
 *  together, and only matching edges attract). */
const computeAlignSnap = (
  near: CanvasRect[],
  rect: CanvasRect,
  axis: 'x' | 'y',
  scale: number,
  lock: number | null,
): AxisSnap => {
  const size = axisSize(rect, axis)
  const positions: number[] = []
  for (const c of near) {
    const cs = axisStart(c, axis)
    const csz = axisSize(c, axis)
    positions.push(cs) //              dragged min edge ↔ candidate min edge
    positions.push(cs + csz - size) // dragged max edge ↔ candidate max edge
  }
  return snapToPositions(positions, axisStart(rect, axis), scale, lock)
}

/**
 * Per-frame canvas snap. Returns the snapped world position of `rect`, whether it snapped (the caller
 * toggles the glide transition on this), and the next hysteresis session. Per axis the 64px GAP wins over
 * alignment; alignment fills only the axes the gap didn't claim (typically the perpendicular one).
 */
export const snapCanvasDrag = (
  rect: CanvasRect,
  candidates: CanvasRect[],
  scale: number,
  session: CanvasSnapSession,
): { x: number; y: number; snapped: boolean; snappedX: boolean; snappedY: boolean; session: CanvasSnapSession } => {
  const near = nearCandidates(rect, candidates, scale)
  if (near.length === 0) {
    return { x: rect.x, y: rect.y, snapped: false, snappedX: false, snappedY: false, session: EMPTY_CANVAS_SNAP_SESSION }
  }

  // 1) Fixed 64px gap first. Judge the y gap from the x-snapped position so "next to" reflects where the
  //    item is actually settling.
  const gx = computeGapSnap(near, rect, 'x', CANVAS_SNAP_GAP, scale, session.gapX)
  const xForY = gx.position ?? rect.x
  const gy = computeGapSnap(near, { ...rect, x: xForY }, 'y', CANVAS_SNAP_GAP, scale, session.gapY)

  // 2) Alignment fills the axes the gap didn't claim (reset that axis's align lock so they never fight).
  const ax: AxisSnap =
    gx.position != null ? { position: null, lock: null } : computeAlignSnap(near, rect, 'x', scale, session.alignX)
  const ay: AxisSnap =
    gy.position != null ? { position: null, lock: null } : computeAlignSnap(near, rect, 'y', scale, session.alignY)

  const x = gx.position ?? ax.position ?? rect.x
  const y = gy.position ?? ay.position ?? rect.y

  // Per-axis snap flags: the caller eases ONLY the axis that actually snapped, so dragging straight along the
  // free axis (while held on the other) follows the cursor 1:1 instead of lagging behind a two-axis glide.
  const snappedX = gx.position != null || ax.position != null
  const snappedY = gy.position != null || ay.position != null
  return {
    x: Math.round(x),
    y: Math.round(y),
    snapped: snappedX || snappedY,
    snappedX,
    snappedY,
    session: { gapX: gx.lock, gapY: gy.lock, alignX: ax.lock, alignY: ay.lock },
  }
}
